import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Message } from 'rsuite'

// Import custom components
import VersatileGrid from '../../ui_reusable/VersatileGrid'
import { HeaderContainer } from '../../ui_reusable/styles/PlacementMisc.styles'
import { SectionTitle } from '../../ui_reusable/styles/SectionTitle.styles'
import ClientProjectPicker from '../../comp_reusable/navigation/ClientProjectPicker'
import { VerySmallNormalTag } from '../../ui_reusable/styles/Tags.styles'
import {
  GridCellBoldText,
  GridCellNormalText,
  GridCellSmallFadedText,
} from '../../ui_reusable/styles/VersatileGridUtils.styles'
import CustomIcons from '../../ui_reusable/CustomIcons'
import SimpleAccordion from '../../ui_reusable/SimpleAccordion'
import useStandardToaster from '../../ui_reusable/StandardToaster'

import DeleteModel from '../comp_reusable/DeleteModel'

// Import context
import { useProjectContext } from '../../../context/ProjectContext'

// Import styled components
import {
  ActionButtonsContainer,
  StatsContainer,
} from './styles/ModelList.styles'
import { HeaderMedium600Style } from '../../../styles/Text.styles'
import theme from '../../../styles/theme'
import { getFitnessStringByScore } from '../../../utils/ScoreFitnessValidation'
import { useClientContext } from '../../../context/ClientContext'
import {
  selectedProjectAllowsModelRun,
  useClientProjectContext,
} from '../../../context/ClientProjectContext'
import ModelLoadingModal from '../comp_reusable/LoadingModelModal'
import { ROUTED_REPORT_MODES } from '../../reports/types/ReportTypes'

const PAGE_SIZE = 10

const ModelListPage = () => {
  const [artifacts, setArtifacts] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1) // 1-based, matches `?page=` API contract
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(null) // { key (backend field), direction } | null
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const { selectedClient } = useClientContext()
  const { selectedProject } = useProjectContext()
  const { clientProjectData } = useClientProjectContext()
  const canRunModels = selectedProjectAllowsModelRun(
    selectedProject,
    clientProjectData,
  )
  const { pushToast } = useStandardToaster()

  const [refresh, setRefresh] = useState(new Date().getTime())

  const [run, setRun] = useState(false)
  const [progress, setProgress] = useState(0)
  const [isPolling, setIsPolling] = useState(false)
  const [solutionName, setSolutionName] = useState('')
  const abortControllerRef = useRef(null)
  const [pendingEditNavigation, setPendingEditNavigation] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedModelToDelete, setSelectedModelToDelete] = useState(null)
  const [isDuplicating, setIsDuplicating] = useState(false)

  const handleClose = () => {
    setRun(false)
    setProgress(0)
    setIsPolling(false)
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
  }

  const handleDeleteClick = (rowData) => {
    setSelectedModelToDelete(rowData)
    setDeleteModalOpen(true)
  }

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false)
    setSelectedModelToDelete(null)
  }

  const handleDuplicate = async (modelId) => {
    try {
      setIsDuplicating(true)

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/artifacts/duplicate/`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ artifact_id: modelId }),
        },
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to duplicate model')
      }

      // Refresh the model list
      setRefresh(new Date().getTime())

      pushToast(
        <Message type="success">Model duplicated successfully</Message>,
        { placement: 'topCenter', duration: 3000 },
      )
    } catch (error) {
      console.error('Error duplicating model:', error)
      pushToast(
        <Message type="error">
          Failed to duplicate model: {error.message}
        </Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    } finally {
      setIsDuplicating(false)
    }
  }

  // Fetch artifacts (one page; server owns filtering/sorting/pagination)
  // FIX: Added AbortController to prevent race condition where a slow unfiltered request
  // could complete after a faster filtered request, overwriting correct data with stale data.
  // When selectedClient/selectedProject change, the previous request is aborted.
  const clientId = selectedClient?.id
  const projectId = selectedProject?.id

  // Global filter changes invalidate the current page
  useEffect(() => {
    setPage(1)
  }, [clientId, projectId])

  useEffect(() => {
    const abortController = new AbortController()

    const fetchArtifacts = async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          page: String(page),
        })

        if (clientId) {
          params.set('client_id', String(clientId))
        }

        if (projectId) {
          params.set('project_id', String(projectId))
        }

        if (search) {
          params.set('search', search)
        }

        if (sort) {
          params.set(
            'ordering',
            `${sort.direction === 'desc' ? '-' : ''}${sort.key}`,
          )
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v2/artifacts/?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )

        // DRF 404s on out-of-range pages (e.g. last item on the page was
        // deleted) — step back to the previous page and let the effect refire.
        if (response.status === 404 && page > 1) {
          setPage(page - 1)
          return
        }

        const result = await response.json()

        setArtifacts(result.data || [])
        setTotal(result.total ?? result.data?.length ?? 0)
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error)
          setArtifacts([])
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      }
    }

    fetchArtifacts()

    return () => {
      abortController.abort()
    }
  }, [projectId, refresh, clientId, page, search, sort])

  // Add effect to handle pending navigation
  useEffect(() => {
    if (pendingEditNavigation) {
      navigate('/models/build', {
        state: {
          solutionId:
            pendingEditNavigation.artifact.project_solution.solution.id,
          artifact: pendingEditNavigation.artifact,
          mode: pendingEditNavigation.mode,
          origin: pendingEditNavigation.origin,
        },
      })
      setPendingEditNavigation(null)
    }
    /* eslint-disable-next-line */
  }, [pendingEditNavigation])

  // Function to flatten models data for search functionality
  const getFlattenedModelsData = () => {
    return artifacts.map((model) => {
      // Use getQualityString from ScoreFitnessValidation.js
      const fitScore = model.fit_score || 0
      const qualityStatus = getFitnessStringByScore(fitScore)

      return {
        ...model,
        solution: model.project_solution?.solution?.name || '',
        project: model.project_solution?.project?.name || '',
        client: model.project_solution?.project?.client?.name || '',
        dependent_driver_name: model.dependent_driver?.name || '',
        independent_drivers_names: '',
        created_by: model.created_by?.name || model.created_by,
        kam: model.project_solution?.project?.kam?.name || '',
        // Add searchable quality status string
        fit_score_quality: qualityStatus,
        // Add variables count
        variables_count: model.independent_drivers_count ?? 0,
      }
    })
  }

  const headers = [
    {
      title: 'Name',
      key: 'name',
      type: 'string',
      isSortable: true,
      isSearchable: true,
      serverKey: 'name',
    },

    {
      title: 'Model',
      key: 'model',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      center: true,
    },

    {
      title: 'Last run date',
      key: 'run_at',
      type: 'datetime',
      isSortable: true,
      isSearchable: false,
      center: true,
      serverKey: 'run_at',
    },
    {
      title: 'Variables',
      key: 'variables_count',
      type: 'number',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
    {
      title: 'Fit score',
      key: 'fit_score_quality', // Use the string quality status
      type: 'string',
      isSortable: false,
      isSearchable: false,
      center: true,
      // Use fit_score for actual numeric sorting
      getSortValue: (row) => row.fit_score,
    },
    {
      title: 'Created by',
      key: 'created_by',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
    {
      title: 'Edit /  Run /  Duplicate /  Delete',
      key: 'actions',
      type: 'none',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
  ]

  // Grid header stats (server `total` — the grid only holds one page now)
  const gridHeader = (
    <StatsContainer>
      <span css={HeaderMedium600Style}>Saved Models: {total}</span>
    </StatsContainer>
  )

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage)
  }, [])

  const handleSearchChange = useCallback((term) => {
    setPage(1)
    setSearch(term)
  }, [])

  const handleSortChange = useCallback((nextSort) => {
    setPage(1)
    setSort(nextSort)
  }, [])

  // Polling function for Optimize task status
  const pollOptimizeTaskStatus = async (taskId, signal) => {
    const maxAttempts = 360 // 360 attempts * 10 seconds = 1 hour max
    let attempts = 0

    while (attempts < maxAttempts) {
      // Check if operation was aborted
      if (signal?.aborted) {
        throw new Error('Polling was cancelled')
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/artifacts/optimize/task/${taskId}/`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal,
          },
        )

        const responseData = await response.json()

        // Check if task is complete (status code 200 means success)
        if (response.status === 200 && responseData.data?.artifact) {
          return responseData.data
        }

        // Check if task failed with backend error message
        if (responseData.data?.status?.toLowerCase() === 'fail') {
          const error = new Error('Optimize task failed')
          error.backendMessage =
            responseData.data?.message || 'Optimize task failed'
          error.isBackendError = true
          throw error
        }

        // Check if task failed
        if (
          responseData.data?.status?.toLowerCase() === 'failure' ||
          responseData.data?.status === 'error'
        ) {
          throw new Error(responseData.data?.message || 'Optimize task failed')
        }

        // Task is still pending (202), wait 10 seconds before next poll
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(resolve, 10000)
          if (signal) {
            signal.addEventListener('abort', () => {
              clearTimeout(timeout)
              reject(new Error('Polling was cancelled'))
            })
          }
        })
        attempts++
      } catch (error) {
        // If operation was aborted, re-throw
        if (
          error.name === 'AbortError' ||
          error.message === 'Polling was cancelled'
        ) {
          throw error
        }

        // If it's a 202 response, continue polling
        if (error.response?.status === 202) {
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(resolve, 10000)
            if (signal) {
              signal.addEventListener('abort', () => {
                clearTimeout(timeout)
                reject(new Error('Polling was cancelled'))
              })
            }
          })
          attempts++
          continue
        }

        // Check if the error response contains a 'fail' status with a message
        if (
          error.response?.data?.status === 'fail' &&
          error.response?.data?.message
        ) {
          const backendError = new Error('Optimize task failed')
          backendError.backendMessage = error.response.data.message
          backendError.isBackendError = true
          throw backendError
        }

        // For other errors, throw
        throw error
      }
    }

    throw new Error('Optimize task timed out after 1 hour')
  }

  const handleRun = async (artifactId) => {
    if (!canRunModels) {
      return
    }
    setRun(true)
    let currentProgress = 0
    let interval = null

    const listArtifact = artifacts.find((elem) => elem.id === artifactId)
    if (!listArtifact) {
      setRun(false)
      pushToast(
        <Message type="error">Could not find model to rerun.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
      return
    }

    const solutionName = (
      listArtifact.solution_type ||
      listArtifact.project_solution?.solution?.name ||
      'impact'
    ).toLowerCase()
    const detailPath =
      solutionName === 'optimize'
        ? `optimize/${listArtifact.id}`
        : `impact/${listArtifact.id}`

    let artifact
    try {
      const detailResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/artifacts/${detailPath}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      )
      if (!detailResponse.ok) {
        throw new Error('Failed to load artifact detail')
      }
      const detailResult = await detailResponse.json()
      artifact = detailResult.data || detailResult
    } catch (error) {
      console.error(error)
      setRun(false)
      pushToast(
        <Message type="error">Could not load model details for rerun.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
      return
    }

    const payload = {
      mode: 'rerun', // Rerun mode: refresh data snapshot hashes without creating new artifact
      id: artifact.id,
      name: artifact.name + '_' + new Date().toISOString().slice(0, 10),
      timeframe: artifact.timeframe,
      project_solution_id: artifact.project_solution?.id,
      dependent_driver_id: artifact.dependent_driver?.id,
      independent_driver_ids:
        artifact.independent_drivers?.map((elem) => {
          return elem.id
        }) ?? [],
      client_id: artifact.client?.id,
      parameters: {
        model_data_frequency: artifact.parameters?.model_data_frequency,
        business_goal: artifact.parameters?.business_goal ?? 'increase',
        aggregation_method: artifact.parameters?.aggregation_method,
      },
      optimize_config: {
        dependent_driver_is_revenue:
          artifact.optimize_config?.dependent_driver_is_revenue ?? true,
        geo_granularity:
          artifact.optimize_config?.geo_granularity ?? 'national',
        budget_type: artifact.optimize_config?.budget_type ?? 'fixed',
        budget_amount: artifact.optimize_config?.budget_amount ?? null,
        max_lag: artifact.optimize_config?.max_lag ?? 8,
        spend_constraint: artifact.optimize_config?.spend_constraint ?? 0.2,
        hill_before_adstock:
          artifact.optimize_config?.hill_before_adstock ?? false,
        roi_impression: artifact.optimize_config?.roi_impression ?? 'standard',
        roi_reach: artifact.optimize_config?.roi_reach ?? 'standard',
        model_data_frequency: artifact.optimize_config?.model_data_frequency,
      },
      channel_driver_inputs: artifact.channel_drivers?.map((elem) => {
        return {
          channel_id: elem.channel?.id,
          spend_dataset_id: elem.spend_dataset?.id,
          impression_dataset_id: elem?.impression_dataset?.id,
          reach_dataset_id: elem?.reach_dataset?.id,
          frequency_dataset_id: elem?.frequency_dataset?.id,
          adstock_decay: elem?.adstock_decay,
        }
      }),
    }

    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Get solution name for routing
    const currentSolutionName =
      artifact.project_solution?.solution?.name?.toLowerCase()
    const solutionType = artifact.project_solution?.solution?.name

    setSolutionName(solutionType)

    // Create a new AbortController
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    try {
      if (solutionType === 'IMPACT') {
        // Impact takes ~30 seconds, update progress every 500ms to reach 95% at 30s
        interval = setInterval(() => {
          currentProgress += 95 / 60 // 95% over 60 intervals (30 seconds)
          if (currentProgress < 95) {
            setProgress(Math.round(currentProgress))
          }
        }, 500)

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/artifacts/predict/${currentSolutionName}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify(payload),
            signal,
          },
        )

        if (interval) clearInterval(interval)

        if (response.ok) {
          const res = await response.json()
          const data = res.data

          setProgress(100)
          setRun(false)

          navigate(`/${currentSolutionName}/report`, {
            state: {
              data: data,
              origin: '/models',
              mode: ROUTED_REPORT_MODES.RERUN_MODEL,
              modelData: artifact,
            },
          })
        } else {
          pushToast(<Message type="error">Failed to run model.</Message>, {
            placement: 'topCenter',
            duration: 5000,
          })
          console.error('Failed to run model:', response)
          setRun(false)
        }
      }

      if (solutionType === 'OPTIMIZE') {
        // Optimize takes ~15 minutes, update progress every 2s to reach 95% at 15min
        interval = setInterval(() => {
          currentProgress += 95 / 450 // 95% over 450 intervals (15 minutes)
          if (currentProgress < 95) {
            setProgress(Math.round(currentProgress))
          }
        }, 2000)

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/artifacts/predict/${currentSolutionName}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify(payload),
            signal,
          },
        )

        if (!response.ok) {
          if (interval) clearInterval(interval)
          pushToast(<Message type="error">Failed to run model.</Message>, {
            placement: 'topCenter',
            duration: 5000,
          })
          console.error('Failed to run model:', response)
          setRun(false)
          return
        }

        const res = await response.json()
        const taskData = res.data
        const taskId = taskData?.task_id

        if (!taskId) {
          if (interval) clearInterval(interval)
          throw new Error('No task_id returned from Optimize model creation')
        }

        // Continue polling for task completion
        setIsPolling(true)

        const reportData = await pollOptimizeTaskStatus(taskId, signal)

        if (interval) clearInterval(interval)
        setIsPolling(false)

        setProgress(100)
        setRun(false)

        navigate(`/${currentSolutionName}/report`, {
          state: {
            data: reportData,
            origin: '/models',
            mode: ROUTED_REPORT_MODES.RERUN_MODEL,
            modelData: artifact,
          },
        })
      }
    } catch (error) {
      if (interval) clearInterval(interval)
      setIsPolling(false)

      if (
        error.name !== 'AbortError' &&
        error.message !== 'Polling was cancelled'
      ) {
        console.error('Error running model:', error)

        // Handle Optimize task backend errors
        if (error.isBackendError && error.backendMessage) {
          pushToast(
            <Message type="error">
              Optimize task failed: {error.backendMessage}
            </Message>,
            {
              placement: 'topCenter',
              duration: 5000,
            },
          )
        } else {
          // Show generic error toast for non-validation errors
          pushToast(
            <Message type="error">
              Error running model: {error.message}
            </Message>,
            {
              placement: 'topCenter',
              duration: 5000,
            },
          )
        }
      }

      setRun(false)
    }
  }

  const handleEdit = async (flattenedArtifact) => {
    const originalArtifact = artifacts.find(
      (a) => a.id === flattenedArtifact.id,
    )

    if (!originalArtifact) {
      console.error('Original artifact not found for ID:', flattenedArtifact.id)
      pushToast(
        <Message type="error">
          Could not edit model. Original data not found.
        </Message>,
        { placement: 'topCenter', duration: 5000 },
      )
      return
    }

    const solutionName = (
      originalArtifact.solution_type ||
      originalArtifact.project_solution?.solution?.name ||
      'impact'
    ).toLowerCase()
    const detailPath =
      solutionName === 'optimize'
        ? `optimize/${originalArtifact.id}`
        : `impact/${originalArtifact.id}`

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/artifacts/${detailPath}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      )
      if (!response.ok) {
        throw new Error('Failed to load artifact detail')
      }
      const result = await response.json()
      const detailArtifact = result.data || result

      setPendingEditNavigation({
        artifact: detailArtifact,
        mode: ROUTED_REPORT_MODES.EDIT_MODEL,
        origin: '/models',
      })
    } catch (error) {
      console.error(error)
      pushToast(
        <Message type="error">
          Could not load model details for editing.
        </Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    }
  }

  return (
    <>
      <HeaderContainer>
        <SectionTitle>Saved models</SectionTitle>
        <ClientProjectPicker />
      </HeaderContainer>

      <SimpleAccordion id={'modelListPageAccordion'} defaultActiveKey={['1']}>
        <SimpleAccordion.Panel eventKey="1" noCollapse>
          <VersatileGrid
            gridContainerStyle={{
              gridTemplateColumns: '1.8fr 0.7fr 0.7fr 0.6fr 0.6fr 1fr 1fr',
            }}
            headers={headers}
            data={getFlattenedModelsData()}
            renderRow={(rowData) => [
              <GridCellBoldText>{rowData.name}</GridCellBoldText>,

              <GridCellNormalText>
                <VerySmallNormalTag>
                  {rowData?.project_solution?.solution.name}
                </VerySmallNormalTag>
              </GridCellNormalText>,
              <GridCellSmallFadedText>
                {new Date(rowData?.run_at).toISOString().slice(0, 10)}
              </GridCellSmallFadedText>,

              <GridCellNormalText>
                {rowData.variables_count}
              </GridCellNormalText>,
              <GridCellNormalText>{rowData?.fit_score}</GridCellNormalText>,
              <GridCellNormalText>
                {rowData?.created_by?.name || rowData.created_by}
              </GridCellNormalText>,
              <ActionButtonsContainer>
                <CustomIcons.PenIcon
                  color={theme.colors.fadedText}
                  size="xs"
                  onClick={() => handleEdit(rowData)}
                  style={{ cursor: 'pointer' }}
                />
                <span
                  title={
                    canRunModels
                      ? undefined
                      : 'Reactivate the project before running a model.'
                  }
                >
                  <CustomIcons.ProcessIcon
                    color={theme.colors.fadedText}
                    size="xs"
                    disabled={!canRunModels}
                    onClick={() => handleRun(rowData?.id)}
                    style={{ cursor: canRunModels ? 'pointer' : 'not-allowed' }}
                  />
                </span>
                <CustomIcons.faCopy
                  onClick={() => handleDuplicate(rowData?.id)}
                  color={
                    isDuplicating
                      ? theme.colors.disabled
                      : theme.colors.fadedText
                  }
                  solid={false}
                  size="xs"
                  style={{
                    cursor: isDuplicating ? 'not-allowed' : 'pointer',
                    opacity: isDuplicating ? 0.6 : 1,
                  }}
                />
                <CustomIcons.TrashIcon
                  color={theme.colors.fadedText}
                  size="xs"
                  onClick={() => handleDeleteClick(rowData)}
                  style={{ cursor: 'pointer' }}
                />
              </ActionButtonsContainer>,
            ]}
            headerInfo={gridHeader}
            maxDisplayedItems={PAGE_SIZE}
            serverSide
            serverTotal={total}
            serverPage={page}
            onPageChange={handlePageChange}
            onSearchChange={handleSearchChange}
            onSortChange={handleSortChange}
            loading={loading}
            loadingMessage={'Loading models...'}
            searchPlaceholder="Find saved model..."
          />
        </SimpleAccordion.Panel>
      </SimpleAccordion>

      <ModelLoadingModal
        open={run}
        progress={progress}
        handleClose={handleClose}
        isPolling={isPolling}
        solutionName={solutionName}
      />

      {selectedModelToDelete && (
        <DeleteModel
          model={selectedModelToDelete}
          setRefresh={setRefresh}
          open={deleteModalOpen}
          onClose={handleCloseDeleteModal}
        />
      )}
    </>
  )
}

export default ModelListPage
